import { useGetConfigQuery } from 'API/applicationConfig/ApplicationConfigAPI';
import { useCallback, useMemo } from 'react';
import { removeSpruceJWTCookie } from 'components/accessControl/removeSpruceJWTCookie';
import { createAuth0ClientForSpruce } from 'ui-toolkit/authentication/methods/createAuth0ClientForSpruce';
import { getAuth0ClientConfigForUserType } from 'ui-toolkit/authentication/methods/getAuth0ClientConfigForUserType';
import { IAuthenticationConfig } from 'ui-toolkit/authentication/types/AuthenticationConfig';
import { eAccessControlLogoutReason } from 'ui-toolkit/enums/eAccessControlLogoutReason';
import { ClientUrlFactory } from 'ui-toolkit/rest/ClientUrlFactory';
import { convertLocationSearchToDataStringParams } from 'utilities/queryString';
import { useGetUserType } from './useGetUserType';
import { datadogRum } from '@datadog/browser-rum';
import { DateTime } from 'luxon';
import { getLatestActivityTime, jwtCookieName, TOKEN_EXP_DATETIME } from 'types/accessControl/AccessControlConstants';
import Cookies from 'js-cookie';
import { decodeOurJWT } from '../decodeOurJWT';
import { eAccessControlUserType } from '../../../ui-toolkit/enums/eAccessControlUserType';

export const usePerformSignoutOperation = (overrideUserType?: eAccessControlUserType) => {
  const myUserType = useGetUserType();
  const { data } = useGetConfigQuery();
  const loginConfigurations = data?.loginConfigurations;

  const userType = useMemo(() => overrideUserType ?? myUserType, [overrideUserType, myUserType]);
  const currentCookie = Cookies.get(jwtCookieName);
  const decodedCookie = useMemo(() => (currentCookie ? decodeOurJWT(currentCookie) : null), [currentCookie]);

  const logout = useCallback(async () => {
    if (!loginConfigurations) {
      return;
    }
    datadogRum.addAction('Logging out', {
      currentTime: DateTime.now().toISO(),
      lastActivityTime: getLatestActivityTime().toISO(),
      cookieExpiration: decodedCookie
        ? new Date(decodedCookie.exp * TOKEN_EXP_DATETIME).toISOString()
        : 'There is no cookie',
    });
    removeSpruceJWTCookie();
    const logoutClientUrlFactory = ClientUrlFactory(window.location.origin);
    const authenticationConfig: IAuthenticationConfig = getAuth0ClientConfigForUserType(userType, loginConfigurations);
    const auth0Client = await createAuth0ClientForSpruce(authenticationConfig, userType);
    let potentialLastLocation: string | undefined = window.location.pathname;
    if (potentialLastLocation === '/spa/logout') {
      const currentLastLocation = convertLocationSearchToDataStringParams(window.location.search)[
        'lastLocation'
      ] as string;
      potentialLastLocation = currentLastLocation === '/spa/logout' ? undefined : currentLastLocation;
    }
    await auth0Client.logout({
      returnTo: logoutClientUrlFactory.urlFor.logoutAuth0(eAccessControlLogoutReason.STANDARD, potentialLastLocation),
    });
  }, [loginConfigurations, userType, decodedCookie]);

  const isReady = !!loginConfigurations && !!userType;
  return { logout, isReady };
};
